import { Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { TOUCH_TARGET_PX } from '../../config/touch-target';
import { stackAlertActionBelowOnMobile } from '../alert-stacked-action-sx';

/** Clients named in the compact notice of a multi-client creation; the rest are counted. */
const MAX_LISTED_CLIENTS = 5;
const PREFIX = 'documents.deadlines.sameType';

/**
 * The latest document opens in a new tab (file, `onOpen`) or the deadline row is shown in the
 * same tab (no file, `href` = internal route).
 */
export type SameTypeLatestAction = { onOpen: () => void } | { href: string };

export interface SameTypeNoticeClient {
  tenantId: string;
  clientName: string;
  activeCount: number;
  archivedCount: number;
  latestAction: SameTypeLatestAction | null;
}

export type SameTypeNoticeAudience = 'consultant' | 'tenant';

interface SameTypeDeadlineNoticeProps {
  /** Clients with at least one deadline of the type, active or archived. */
  clients: readonly SameTypeNoticeClient[];
  /** Several clients selected: compact list instead of the single-client sentence. */
  isMultiClient: boolean;
  audience?: SameTypeNoticeAudience;
  /** Called when the link moves to an in-app route: the hosting dialog closes. */
  onNavigate?: () => void;
}

function LatestLink({
  action,
  label,
  onNavigate,
}: {
  action: SameTypeLatestAction;
  label: string;
  onNavigate?: () => void;
}) {
  const commonProps = {
    color: 'info',
    size: 'small',
    sx: { minHeight: TOUCH_TARGET_PX, whiteSpace: 'nowrap' },
    'data-testid': 'same-type-notice-latest',
  } as const;
  if ('href' in action) {
    // Rotta interna dell'app: stessa scheda via router, senza un secondo avvio della SPA.
    return (
      <Button {...commonProps} component={RouterLink} to={action.href} onClick={onNavigate}>
        {label}
      </Button>
    );
  }
  return (
    <Button {...commonProps} onClick={action.onOpen}>
      {label}
    </Button>
  );
}

function hasAnyDeadline(noticeClient: SameTypeNoticeClient): boolean {
  return noticeClient.activeCount + noticeClient.archivedCount > 0;
}

/**
 * Informative only (never blocks the creation): the chosen client(s) already have deadlines of
 * this document type. "Documents" are counted as active deadlines, archived ones apart.
 */
export function SameTypeDeadlineNotice({
  clients,
  isMultiClient,
  audience = 'consultant',
  onNavigate,
}: SameTypeDeadlineNoticeProps) {
  const { t } = useTranslation();
  const listedClients = clients.filter(hasAnyDeadline);
  if (listedClients.length === 0) {
    return null;
  }
  const onlyClient = listedClients.length === 1 ? listedClients[0] : undefined;
  // "Vedi" when the link can only lead to one document in all; otherwise it is the latest of many.
  const isSingleDocument =
    onlyClient !== undefined && onlyClient.activeCount + onlyClient.archivedCount === 1;
  const latestLabel = isSingleDocument
    ? t(`${PREFIX}.view`, { defaultValue: 'Vedi' })
    : t(`${PREFIX}.viewLatest`, { defaultValue: "Vedi l'ultimo" });

  return (
    <Alert
      severity="info"
      data-testid="same-type-notice"
      sx={stackAlertActionBelowOnMobile}
      action={
        onlyClient?.latestAction ? (
          <LatestLink
            action={onlyClient.latestAction}
            label={latestLabel}
            onNavigate={onNavigate}
          />
        ) : undefined
      }
    >
      {isMultiClient || !onlyClient ? (
        <MultiClientText clients={listedClients} />
      ) : (
        <SingleClientText client={onlyClient} audience={audience} />
      )}
    </Alert>
  );
}

/** The consultant reads about "this client"; the company reads about itself. */
const SINGLE_CLIENT_TEXTS = {
  consultant: {
    keyPrefix: PREFIX,
    subject: 'Questo cliente ha già',
    archivedSubject: 'Questo cliente ha',
  },
  tenant: {
    keyPrefix: `${PREFIX}.tenant`,
    subject: 'Hai già',
    archivedSubject: 'Hai',
  },
} as const;

function SingleClientText({
  client,
  audience,
}: {
  client: SameTypeNoticeClient;
  audience: SameTypeNoticeAudience;
}) {
  const { t } = useTranslation();
  const { keyPrefix, subject, archivedSubject } = SINGLE_CLIENT_TEXTS[audience];
  if (client.activeCount === 0) {
    return (
      <>
        {t(`${keyPrefix}.archivedOnly`, {
          count: client.archivedCount,
          defaultValue_one: `${archivedSubject} {{count}} documento di questo tipo archiviato.`,
          defaultValue_other: `${archivedSubject} {{count}} documenti di questo tipo archiviati.`,
        })}
      </>
    );
  }
  if (client.activeCount === 1 && client.archivedCount === 0) {
    return (
      <>
        {t(`${keyPrefix}.single`, {
          defaultValue: `${subject} un documento di questo tipo.`,
        })}
      </>
    );
  }
  if (client.archivedCount === 0) {
    return (
      <>
        {t(`${keyPrefix}.many`, {
          count: client.activeCount,
          defaultValue_one: `${subject} {{count}} documento di questo tipo.`,
          defaultValue_other: `${subject} {{count}} documenti di questo tipo.`,
        })}
      </>
    );
  }
  return (
    <>
      {t(`${keyPrefix}.manyWithArchived`, {
        count: client.activeCount,
        archivedCount: client.archivedCount,
        defaultValue_one: `${subject} {{count}} documento di questo tipo (archiviati: {{archivedCount}}).`,
        defaultValue_other: `${subject} {{count}} documenti di questo tipo (archiviati: {{archivedCount}}).`,
      })}
    </>
  );
}

function MultiClientText({ clients }: { clients: readonly SameTypeNoticeClient[] }) {
  const { t } = useTranslation();
  const shownClients = clients.slice(0, MAX_LISTED_CLIENTS);
  const otherCount = clients.length - shownClients.length;
  const shownText = shownClients
    .map((shownClient) => {
      // Active count when there are active ones, else the archived ones: never "(0)".
      const countLabel =
        shownClient.activeCount > 0
          ? String(shownClient.activeCount)
          : t(`${PREFIX}.archivedEntry`, {
              count: shownClient.archivedCount,
              defaultValue_one: '{{count}} archiviato',
              defaultValue_other: '{{count}} archiviati',
            });
      return `${shownClient.clientName} (${countLabel})`;
    })
    .join(', ');
  const othersText =
    otherCount > 0
      ? ` ${t(`${PREFIX}.andOthers`, {
          count: otherCount,
          defaultValue_one: 'e un altro',
          defaultValue_other: 'e altri {{count}}',
        })}`
      : '';
  return (
    <>
      <span>
        {t(`${PREFIX}.clientsIntro`, {
          count: clients.length,
          defaultValue_one: 'Un cliente ha già documenti di questo tipo:',
          defaultValue_other: '{{count}} clienti hanno già documenti di questo tipo:',
        })}
      </span>{' '}
      <span>
        {shownText}
        {othersText}.
      </span>
    </>
  );
}
